import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

/**
 * The AI transparency notice, required by **EU AI Act Art. 50(1)**: a person must be informed that
 * they are interacting with an AI system, clearly and distinguishably, at the latest at the time of
 * the first interaction — unless it is obvious to a reasonably observant user. A help widget that
 * looks like any other support chat is not obvious, so the panel says so outright.
 *
 * It therefore sits **outside** the scrolling transcript and outside the empty state: it must still
 * be there on the tenth answer, not only before the first question. It also names the route to a
 * human (support), which is what keeps the disclosure honest rather than decorative.
 */
export const AiDisclosure: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<div
			className="flex items-start gap-2 border-b border-border-light bg-gray-50 px-4 py-2"
			data-testid="help-ai-disclosure"
			role="note"
		>
			<Icon className="mt-0.5 shrink-0 text-xs text-text-meta" icon={faSparkles} />
			<p className="text-xs leading-relaxed text-text-meta">{t("helpCenter.ai.disclosure")}</p>
		</div>
	);
};

/** Marks one answer as machine-generated, so a transcript read on its own stays unambiguous. */
export const AiAnswerBadge: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<span className="flex items-center gap-1 pl-1 text-[10px] font-semibold uppercase tracking-wide text-text-meta">
			<Icon icon={faSparkles} />
			{t("helpCenter.ai.generatedAnswer")}
		</span>
	);
};
